/**
 * Low Stock Alert Component - Catalyst Design System
 *
 * Purpose: Display sophisticated inventory messaging for cannabis products
 * Design: Matches Catalyst's premium aesthetic with subtle urgency psychology
 *
 * Features:
 * - Cannabis-appropriate messaging (no aggressive "hurry" language)
 * - Catalyst design system integration (font-helvetica-neue, subtle shadows)
 * - Three-tier messaging system based on inventory levels
 * - Professional tone suitable for dispensary customers
 * - Responsive design matching product page styling
 *
 * Usage: <LowStockAlert stock={product.stock} sellableQuantity={product.sellable_quantity} />
 *
 * Related files:
 * - ~/api/typesense.server.ts - Product interface with stock fields
 * - ~/routes/_layout.product.$slug/route.tsx - Product page implementation
 */

import React from 'react';

interface LowStockAlertProps {
  stock?: number;
  sellableQuantity?: number;
  className?: string;
}

const LowStockAlert: React.FC<LowStockAlertProps> = ({
  stock,
  sellableQuantity,
  className = '',
}) => {
  // Prefer sellable_quantity if available, fallback to stock
  // sellable_quantity is likely the actual customer-facing inventory
  const displayStock = sellableQuantity ?? stock;

  // Don't show if no stock data or above threshold
  if (!displayStock || displayStock > 10) {
    return null;
  }

  // Real e-commerce messaging like major retailers use
  const getStockMessage = (stockLevel: number) => {
    if (stockLevel <= 2) {
      return {
        message: 'Almost sold out',
        subMessage: 'Order now while supplies last',
        urgency: 'critical',
        bgColor: 'bg-gradient-to-r from-red-50 to-red-100',
        textColor: 'text-red-900',
        borderColor: 'border-red-200',
        accentColor: 'text-red-700',
        icon: '⚠️',
      };
    } else if (stockLevel <= 5) {
      return {
        message: 'Low stock',
        subMessage: 'Order soon to avoid disappointment',
        urgency: 'high',
        bgColor: 'bg-gradient-to-r from-amber-50 to-orange-50',
        textColor: 'text-amber-900',
        borderColor: 'border-amber-200',
        accentColor: 'text-amber-700',
        icon: '⏰',
      };
    } else {
      return {
        message: 'Limited stock available',
        subMessage: 'Popular item - order today',
        urgency: 'medium',
        bgColor: 'bg-gradient-to-r from-blue-50 to-indigo-50',
        textColor: 'text-blue-900',
        borderColor: 'border-blue-200',
        accentColor: 'text-blue-700',
        icon: '📦',
      };
    }
  };

  const stockInfo = getStockMessage(displayStock);

  return (
    <div
      className={`relative overflow-hidden rounded-lg border ${stockInfo.borderColor} ${stockInfo.bgColor} shadow-[0px_1px_3px_rgba(0,0,0,0.1)] transition-all duration-200 hover:shadow-[0px_2px_6px_rgba(0,0,0,0.15)] ${className} `}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3 p-4">
        {/* Icon */}
        <div className={`flex-shrink-0 text-lg ${stockInfo.accentColor}`}>{stockInfo.icon}</div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div
            className={`font-helvetica-neue text-sm leading-5 font-semibold ${stockInfo.textColor}`}
          >
            {stockInfo.message}
          </div>
          <div
            className={`font-helvetica-neue mt-1 text-xs leading-4 ${stockInfo.accentColor} opacity-90`}
          >
            {stockInfo.subMessage}
          </div>
        </div>
      </div>

      {/* Subtle accent line */}
      <div
        className={`absolute right-0 bottom-0 left-0 h-0.5 ${stockInfo.accentColor.replace('text-', 'bg-')} opacity-30`}
      />
    </div>
  );
};

export default LowStockAlert;
